import { useCallback, useEffect, useState } from 'react'
import { SearchInput } from '@/components/ui/SearchInput'
import { Badge } from '@/components/ui/Badge'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { Container, ContainerContent, ContainerHeader, ContainerTitle } from '@/components/ui/Container'
import { Button } from '@/components/ui/Button'
import { Pagination } from '@/components/ui/Pagination'
import { SegmentedControl, SegmentedControlItem } from '@/components/ui/SegmentedControl'
import { getAuthenticatedUserProfile } from '@/services/api'
import { useOrganizations } from '@/features/admin/organizationsQueries'
import { CreateOrganizationDialog } from '@/features/admin/components/CreateOrganizationDialog'
import type { AdminOrganizationListItem } from '@/types/admin'

function Row({ item, onClick }: { item: AdminOrganizationListItem; onClick: () => void }) {
  return (
    <tr
      className="border-t border-gray-100 transition-colors hover:bg-gray-50 cursor-pointer"
      onClick={onClick}
    >
      <td className="px-4 py-3 font-medium text-gray-900">{item.name}</td>
      <td className="hidden md:table-cell px-4 py-3 text-sm text-gray-500">{item.tenant_id}</td>
      <td className="px-4 py-3 text-sm text-gray-600">
        <Badge tone={item.type === 'partner' ? 'info' : 'neutral'}>
          {item.type === 'partner' ? 'Partner' : 'Standard'}
        </Badge>
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">{item.workflow_count}</td>
    </tr>
  )
}

export function OrganizationListPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [createOpen, setCreateOpen] = useState(false)

  const { data: profile } = useQuery({
    queryKey: ['user-profile'],
    queryFn: getAuthenticatedUserProfile,
  })
  const isRoot = profile?.user.organization.root_organization ?? false

  const page = Number(searchParams.get('page')) || 1
  const searchQuery = searchParams.get('q') ?? ''
  const scope = isRoot && searchParams.get('scope') === 'all' ? 'all' : 'subtree'
  const typeFilter = (searchParams.get('type') as 'standard' | 'partner' | null) ?? 'all'
  const [searchInput, setSearchInput] = useState(searchQuery)

  const { data, isLoading } = useOrganizations({
    page,
    q: searchQuery || undefined,
    scope: isRoot ? scope : undefined,
    type: typeFilter === 'all' ? undefined : typeFilter,
  })

  const commitSearch = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (value) next.set('q', value)
          else next.delete('q')
          next.delete('page')
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  useEffect(() => {
    if (searchInput === searchQuery) return
    const timer = setTimeout(() => commitSearch(searchInput), 300)
    return () => clearTimeout(timer)
  }, [searchInput, searchQuery, commitSearch])

  useEffect(() => {
    setSearchInput(searchQuery)
  }, [searchQuery])

  function updateScope(next: string) {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        if (next === 'all') params.set('scope', 'all')
        else params.delete('scope')
        params.delete('page')
        return params
      },
      { replace: true },
    )
  }

  function updateTypeFilter(next: string) {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        if (next === 'all') params.delete('type')
        else params.set('type', next)
        params.delete('page')
        return params
      },
      { replace: true },
    )
  }

  function handlePage(p: number) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (p > 1) next.set('page', String(p))
        else next.delete('page')
        return next
      },
      { replace: true },
    )
  }

  const items = data?.data ?? []

  return (
    <div className="container mx-auto py-4">
      <Container>
        <ContainerHeader className="flex flex-row items-center justify-between space-y-0">
          <ContainerTitle>Organizations</ContainerTitle>
          <Button variant="cta" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" />
            Create organization
          </Button>
        </ContainerHeader>
        <ContainerContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <SearchInput
              className="w-[220px]"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search organizations…"
            />
            <SegmentedControl value={typeFilter} onValueChange={updateTypeFilter}>
              <SegmentedControlItem value="all" label="All" />
              <SegmentedControlItem value="standard" label="Standard" />
              <SegmentedControlItem value="partner" label="Partner" />
            </SegmentedControl>
            <div className="flex-1" />
            {isRoot && (
              <SegmentedControl value={scope} onValueChange={updateScope}>
                <SegmentedControlItem value="subtree" label="My subtree" />
                <SegmentedControlItem value="all" label="All organizations" />
              </SegmentedControl>
            )}
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-16 text-sm text-gray-400">Loading…</div>
          ) : (
            <div className="data-sheet">
            <table className="w-full text-left">
              <thead>
                <tr className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <th className="px-4 pb-2 pt-3">Name</th>
                  <th className="hidden md:table-cell px-4 pb-2 pt-3">Tenant ID</th>
                  <th className="px-4 pb-2 pt-3">Type</th>
                  <th className="px-4 pb-2 pt-3">Workflows</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-400">
                      No organizations found
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <Row
                      key={item.id}
                      item={item}
                      onClick={() => navigate(`/admin/workflows?organization=${item.id}`)}
                    />
                  ))
                )}
              </tbody>
            </table>
            </div>
          )}

          {data && (
            <Pagination page={data.page} totalCount={data.total} pageSize={data.page_size} onPage={handlePage} />
          )}
        </ContainerContent>
      </Container>

      <CreateOrganizationDialog open={createOpen} onOpenChange={setCreateOpen} canCreatePartner={isRoot} />
    </div>
  )
}
